import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Contractul ecranului Settings -> CI rules (v4/api, `App\Settings\CiRules`).
 *
 * Numele campurilor sunt EXACT cele pe care le scrie serverul, si forma
 * raspunsului e inghetata intr-o inregistrare comuna
 * (`src/settings/ci_rules/__fixtures__/ciRulesPage.json`), citita si de proba
 * din browser, si de cea din PHP. Cusatura dintre cele doua jumatati s-a rupt
 * de cinci ori saptamana asta cu AMANDOUA suitele verzi, fiindca fiecare parte
 * se proba numai pe sine.
 */

export interface Pair {
  value: number
  label: string
}

/** O regula de vocabular, plus cate conditii poarta jumatatea ei automata. */
export interface CiRule {
  ID: number
  relation_type_id: number
  relation_name: string
  relation_code: string
  source_class_id: number | null
  target_class_id: number | null
  source_class_name: string
  target_class_name: string
  source_kind: number | null
  target_kind: number | null
  min_cardinality: number
  max_cardinality: number | null
  status: number
  auto_conditions: number
  auto_confidence: number | null
}

export interface CiThreshold {
  ID: number
  company_id: number | null
  company_name: string
  scope_type: number
  scope_id: number
  scope_label: string
  scope_name: string
  min_observations: number
  min_confidence: number
  new_edges_per_hour_alert: number | null
  /** null = nescris (0/NULL in baza): se aplica `defaults.staleMinutes`. */
  stale_minutes: number | null
  status: number
}

export interface CiSubscriber {
  ID: number
  address: string
  company_id: number | null
  company_name: string
  on_appeared: number
  on_disappeared: number
  on_surge: number
  min_changes: number
  status: number
  last_sent_at: string | null
}

export interface CiRelationType {
  id: number
  code: string
  name: string
  inverse_name: string
}

export interface CiRuleClass {
  id: number
  name: string
  kind: number
}

export interface CiRulesAlarm {
  key: string
  label: string
  flag: number
  flag_name: string
  color: string
}

export interface CiRulesPage {
  rules: CiRule[]
  thresholds: CiThreshold[]
  subscribers: CiSubscriber[]
  pending: { waiting: number; subscribers: number }
  /** Mailul cu schimbarile neautorizate pe echipamente (cerinta 86); implicit pornit. */
  unauthorizedEmail: boolean
  /** Cerinta 97: responsabilii elementelor primesc si ei schimbarile. */
  notifyOwners: boolean
  /** Comutatorul e al furnizorului: numai cine vede toti clientii il schimba. */
  canNotifyOwners: boolean
  companies: { id: number; name: string }[]
  classes: CiRuleClass[]
  types: CiRelationType[]
  kinds: Pair[]
  scopes: Pair[]
  alarms: CiRulesAlarm[]
  defaults: { observations: number; confidence: number; staleMinutes: number }
  onlyAuto: boolean
  canWrite: boolean
  canDelete: boolean
  canThresholds: boolean
  canEditThresholds: boolean
  canDeleteThresholds: boolean
}

/** Cheile de proprietate ale unei clase, oferite ca lista in fereastra automata. */
export interface CiRuleField {
  key: string
  label: string
}

export interface CiRuleAutoForm {
  rule: CiRule
  conditions: { source: string; target: string }[]
  confidence: number
  sides: { value: string; label: string }[]
  sourceFields: CiRuleField[]
  targetFields: CiRuleField[]
}

export interface WriteResult {
  status: 'OK' | 'ERROR'
  message: string
  id?: number
}

const BASE = '/settings/ci_rules'

export function ciRulesQuery(onlyAuto: boolean) {
  return queryOptions({
    queryKey: ['settings', 'ci_rules', onlyAuto],
    queryFn: () => apiGet<CiRulesPage>(`${BASE}${onlyAuto ? '?automatic=1' : ''}`),
  })
}

export function ciRuleAutoQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'ci_rules', 'auto', id],
    queryFn: () => apiGet<CiRuleAutoForm>(`${BASE}/rules/${id}/auto`),
    enabled: id > 0,
  })
}

export function saveCiRule(fields: Record<string, PostValue>) {
  return apiPost<WriteResult>(`${BASE}/rules`, fields)
}

export function removeCiRule(id: number) {
  return apiPost<WriteResult>(`${BASE}/rules/${id}/remove`, {})
}

/**
 * Conditiile pleaca ca doua siruri paralele (`source[]` / `target[]`), exact
 * cum le trimitea formularul vechi - serverul le impleteste inapoi.
 */
export function saveCiAutoRule(id: number, conditions: { source: string; target: string }[], confidence: number) {
  return apiPost<WriteResult>(`${BASE}/rules/${id}/auto`, {
    source: conditions.map((c) => c.source),
    target: conditions.map((c) => c.target),
    confidence,
  })
}

export function saveCiThreshold(fields: Record<string, PostValue>) {
  return apiPost<WriteResult>(`${BASE}/thresholds`, fields)
}

export function removeCiThreshold(id: number) {
  return apiPost<WriteResult>(`${BASE}/thresholds/${id}/remove`, {})
}

export function saveCiSubscriber(fields: Record<string, PostValue>) {
  return apiPost<WriteResult>(`${BASE}/subscribers`, fields)
}

export function removeCiSubscriber(id: number) {
  return apiPost<WriteResult>(`${BASE}/subscribers/${id}/remove`, {})
}

/** Porneste / opreste mailul cu schimbari neautorizate, pentru tot furnizorul. */
export function saveCiAlerts(on: boolean) {
  return apiPost<WriteResult>(`${BASE}/alerts`, { email_unauthorized: on ? 1 : 0 })
}

export function saveCiNotifyOwners(on: boolean) {
  return apiPost<WriteResult>(`${BASE}/notify_owners`, { notify_owners: on ? 1 : 0 })
}
